import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Checkbox,
  CheckboxState,
} from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Toggle } from '@delmonicos/shared-design-system-react/src/components/toggle/toggle';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { DayOfWeek } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { addMinutes, format, parse, subMinutes } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  RangeUIModel,
  useTariffUIContext,
} from '@app/feature/tariff/tariff.context';

const addOneMinute = (timeString: string): string => {
  const date = parse(timeString, 'HH:mm', new Date());
  return format(addMinutes(date, 1), 'HH:mm');
};

const subtractOneMinute = (timeString: string): string => {
  const date = parse(timeString, 'HH:mm', new Date());
  return format(subMinutes(date, 1), 'HH:mm');
};

interface DaysSelectorProps {
  range: RangeUIModel;
  unauthorizedDays: DayOfWeek[];
  setRange: (value: RangeUIModel) => void;
  id: string;
}

const DaysSelector = ({
  range,
  setRange,
  unauthorizedDays,
  id,
}: DaysSelectorProps): React.JSX.Element => {
  const { t } = useTranslation();

  const selectedDays = range?.daysOfWeek || [];

  const allWeekDays = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
  ];

  const allWeekEndDays = [DayOfWeek.SATURDAY, DayOfWeek.SUNDAY];

  const isAllWeekDaysSelected = allWeekDays.every((day) =>
    selectedDays.includes(day),
  );
  const isAllWeekEndSelected = allWeekEndDays.every((day) =>
    selectedDays.includes(day),
  );
  const isAllWeekDaysDisabled = allWeekDays.every((day) =>
    unauthorizedDays.includes(day),
  );
  const isAllWeekendDaysDisabled = allWeekEndDays.every((day) =>
    unauthorizedDays.includes(day),
  );

  const updateDaysInContext = useCallback(
    (days: DayOfWeek[]) => {
      setRange({
        ...range,
        daysOfWeek: days,
      });
    },
    [range, setRange],
  );

  const handleWeekDaysChange = (checked: boolean) => {
    const weekendDays = selectedDays.filter((day) =>
      allWeekEndDays.includes(day),
    );
    const availableWeekDays = allWeekDays.filter(
      (day) => !unauthorizedDays.includes(day),
    );
    const newDays = checked
      ? [...availableWeekDays, ...weekendDays]
      : weekendDays;
    updateDaysInContext(newDays);
  };

  useEffect(() => {
    if (!range?.daysOfWeek) return;
    const filteredDays = range.daysOfWeek.filter(
      (day) => !unauthorizedDays.includes(day),
    );
    if (filteredDays.length !== range.daysOfWeek.length) {
      updateDaysInContext(filteredDays);
    }
  }, [unauthorizedDays, range.daysOfWeek, updateDaysInContext]);

  const handleWeekEndChange = (checked: boolean) => {
    const weekDays = selectedDays.filter((day) => allWeekDays.includes(day));
    const availableWeekendDays = allWeekEndDays.filter(
      (day) => !unauthorizedDays.includes(day),
    );
    const newDays = checked ? [...weekDays, ...availableWeekendDays] : weekDays;
    updateDaysInContext(newDays);
  };

  const handleDayChange = (day: DayOfWeek, checked: boolean) => {
    const newDays = checked
      ? [...selectedDays, day]
      : selectedDays.filter((d) => d !== day);
    updateDaysInContext(newDays);
  };

  return (
    <Stack gap={GapEnum.M}>
      <Stack gap={GapEnum.M} direction="column" flex={1}>
        <Checkbox
          id={`range-${id}-weekdays`}
          label={t('$.tariff-update.step-2.range.weekdays.title')}
          checked={isAllWeekDaysSelected}
          onChange={(e) => handleWeekDaysChange(e.target.checked)}
          state={
            isAllWeekDaysDisabled
              ? CheckboxState.Disabled
              : CheckboxState.Default
          }
        />

        {allWeekDays.map((day) => (
          <Checkbox
            key={day}
            id={`range-${id}-${day.toLowerCase()}`}
            label={t(
              `delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.${day}`,
            )}
            checked={selectedDays.includes(day)}
            onChange={(e) => handleDayChange(day, e.target.checked)}
            state={
              unauthorizedDays.includes(day)
                ? CheckboxState.Disabled
                : CheckboxState.Default
            }
          />
        ))}
      </Stack>

      <Stack gap={GapEnum.M} direction="column" flex={1}>
        <Checkbox
          id={`range-${id}-weekend`}
          label={t('$.tariff-update.step-2.range.weekend.title')}
          checked={isAllWeekEndSelected}
          onChange={(e) => handleWeekEndChange(e.target.checked)}
          state={
            isAllWeekendDaysDisabled
              ? CheckboxState.Disabled
              : CheckboxState.Default
          }
        />

        {allWeekEndDays.map((day) => (
          <Checkbox
            key={day}
            id={`range-${id}-${day.toLowerCase()}`}
            label={t(
              `delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.${day}`,
            )}
            checked={selectedDays.includes(day)}
            onChange={(e) => handleDayChange(day, e.target.checked)}
            state={
              unauthorizedDays.includes(day)
                ? CheckboxState.Disabled
                : CheckboxState.Default
            }
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface TimeSlotSelectorProps {
  range: RangeUIModel;
  setRange: (value: RangeUIModel) => void;
  elementIndex: number;
}

const TimeSlotSelector = ({
  range,
  setRange,
  elementIndex,
}: TimeSlotSelectorProps): React.JSX.Element => {
  const { t } = useTranslation();

  const secondTimeSlotStart = useMemo(
    () => range?.secondTimeSlot?.startTime,
    [range.secondTimeSlot.startTime],
  );
  const secondTimeSlotEnd = useMemo(
    () => range?.secondTimeSlot?.endTime,
    [range.secondTimeSlot.endTime],
  );

  const handleToggle = (checked: boolean) => {
    const newRange = { ...range };
    newRange.hasDoubleTimeSlot = checked;
    setRange(newRange);
  };

  const handleStartTimeChange = (time: string) => {
    const newRange = { ...range };
    newRange.firstTimeSlot.startTime = time;
    newRange.secondTimeSlot.endTime = subtractOneMinute(time);

    setRange(newRange);
  };

  const handleEndTimeChange = (time: string) => {
    const newRange = { ...range };
    newRange.firstTimeSlot.endTime = time;
    newRange.secondTimeSlot.startTime = addOneMinute(time);

    setRange(newRange);
  };

  return (
    <>
      <Stack gap={GapEnum.M} justifyContent="space-between" alignItems="center">
        <AccentTitle
          icon="clock"
          text={t('$.tariff-update.step-2.time-slot.title')}
          variant={AccentVariantEnum.Secondary}
        />
        <Toggle
          id={`time-slot-${elementIndex}`}
          checked={range.hasDoubleTimeSlot}
          onChange={handleToggle}
        />
      </Stack>

      {range.hasDoubleTimeSlot && (
        <Stack gap={GapEnum.M}>
          <Stack gap={GapEnum.M} direction="column" flex={1}>
            <Text bold>
              {t('$.tariff-update.step-2.time-slot.first.title')}
            </Text>
            <Stack gap={GapEnum.M}>
              <TextInput
                label={t('$.tariff-update.step-2.time-slot.first.start-hour')}
                required
                type="time"
                value={range?.firstTimeSlot?.startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              />
              <TextInput
                label={t('$.tariff-update.step-2.time-slot.first.end-hour')}
                required
                type="time"
                value={range?.firstTimeSlot?.endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
              />
            </Stack>
          </Stack>
          <Stack gap={GapEnum.M} direction="column" flex={1}>
            <Text bold>
              {t('$.tariff-update.step-2.time-slot.second.title')}
            </Text>
            <Stack gap={GapEnum.M}>
              <TextInput
                label={t('$.tariff-update.step-2.time-slot.second.start-hour')}
                disabled
                type="time"
                value={secondTimeSlotStart}
                onChange={() => {}}
              />
              <TextInput
                label={t('$.tariff-update.step-2.time-slot.second.end-hour')}
                disabled
                type="time"
                value={secondTimeSlotEnd}
                onChange={() => {}}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </>
  );
};

const allDays = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

const inversedDays = (days: DayOfWeek[]): DayOfWeek[] => {
  return allDays.filter((day) => !days.includes(day));
};

const TariffUpdateFormStep2Range1 = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { firstRange, setFirstRange, setSecondRange, secondRange } =
    useTariffUIContext();

  const { setValue } = useFormContext<UpdateTariffModel>();

  useEffect(() => {
    setValue(`tariff.elements.0.restrictions`, {
      dayOfWeek: firstRange.daysOfWeek,
      startTime: firstRange.firstTimeSlot.startTime,
      endTime: firstRange.firstTimeSlot.endTime,
    });
    setValue(`tariff.elements.1.restrictions`, {
      dayOfWeek: firstRange.daysOfWeek,
      startTime: firstRange.secondTimeSlot.startTime,
      endTime: firstRange.secondTimeSlot.endTime,
    });
  }, [setValue, firstRange, firstRange.firstTimeSlot]);

  const handleSetFirstRange = (newFirstRange: RangeUIModel) => {
    setFirstRange(newFirstRange);

    const secondRangeDays: DayOfWeek[] = inversedDays(
      newFirstRange.daysOfWeek ?? [],
    );

    setSecondRange({
      ...secondRange,
      daysOfWeek: secondRangeDays,
    });

    setValue(`tariff.elements.2.restrictions`, {
      dayOfWeek: secondRangeDays,
    });
    setValue(`tariff.elements.3.restrictions`, {
      dayOfWeek: secondRangeDays,
    });
  };

  return (
    <Card gap={GapEnum.M} flex={1}>
      <Stack gap={GapEnum.M} alignItems="center">
        <AccentTitle
          icon="calendar-date"
          text={t('$.tariff-update.step-2.range.title', { number: '1' })}
          variant={AccentVariantEnum.Secondary}
        />
        <Text color={TextColor.Error}>*</Text>
      </Stack>

      <DaysSelector
        range={firstRange}
        setRange={handleSetFirstRange}
        unauthorizedDays={[]}
        id="1"
      />

      <Divider />

      <TimeSlotSelector
        range={firstRange}
        setRange={setFirstRange}
        elementIndex={1}
      />
    </Card>
  );
};

const TariffUpdateFormStep2Range2 = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { firstRange, secondRange, setSecondRange } = useTariffUIContext();
  const { setValue } = useFormContext<UpdateTariffModel>();

  useEffect(() => {
    setValue(`tariff.elements.2.restrictions`, {
      dayOfWeek: secondRange.daysOfWeek,
      startTime: secondRange.firstTimeSlot.startTime,
      endTime: secondRange.firstTimeSlot.endTime,
    });
    setValue(`tariff.elements.3.restrictions`, {
      dayOfWeek: secondRange.daysOfWeek,
      startTime: secondRange.secondTimeSlot.startTime,
      endTime: secondRange.secondTimeSlot.endTime,
    });
  }, [setValue, secondRange, secondRange.firstTimeSlot]);

  const unauthorizedDays = useMemo(() => {
    return allDays.filter((d) => firstRange.daysOfWeek?.includes(d));
  }, [firstRange]);

  return (
    <Card gap={GapEnum.M} flex={1}>
      <Stack gap={GapEnum.M} alignItems="center">
        <AccentTitle
          icon="calendar-date"
          text={t('$.tariff-update.step-2.range.title', { number: '2' })}
          variant={AccentVariantEnum.Accent1}
        />
        <Text color={TextColor.Error}>*</Text>
      </Stack>

      <DaysSelector
        range={secondRange}
        setRange={() => {}}
        unauthorizedDays={unauthorizedDays}
        id="2"
      />

      <Divider />

      <TimeSlotSelector
        range={secondRange}
        setRange={setSecondRange}
        elementIndex={2}
      />
    </Card>
  );
};

export const TariffUpdateFormStep2Component = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.M} flex={1}>
      <TariffUpdateFormStep2Range1 />
      <TariffUpdateFormStep2Range2 />
    </Stack>
  );
};
